import { useMemo } from "react";
import LazyApexChart from "../../../client/components/LazyApexChart";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import type { WidgetViewProps } from "../types";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function fmtShort(v: number): string {
  return Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(1);
}

export default function SizingAnalysisWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { analyticsData, isDark, themeId, t } = useDashboardData();

  const tableColumns = [
    { key: "tradeIndex", label: t.analytics.tradeLabel, align: "left" as const },
    { key: "size", label: t.analytics.sizeLabel, align: "right" as const },
    { key: "pnl", label: t.analytics.pnlLabel, align: "right" as const },
  ];
  const tableRows = useMemo(() => {
    let points = analyticsData?.sizingData ?? [];
    // Cap the table to ~100 rows by keeping every Nth point.
    if (points.length > 100) {
      const step = Math.ceil(points.length / 100);
      points = points.filter((_, i) => i % step === 0);
    }
    return points.map((s) => ({
      tradeIndex: `#${s.tradeIndex}`,
      size: s.absSize.toFixed(2),
      pnl: (
        <span className={s.pnl >= 0 ? "text-pnlPositive" : "text-pnlNegative"}>
          {s.pnl >= 0 ? "+" : ""}
          {s.pnl.toFixed(2)}
        </span>
      ),
    }));
  }, [analyticsData]);

  const built = useMemo(() => {
    if (!analyticsData || analyticsData.sizingData.length === 0) return null;

    let points = analyticsData.sizingData;
    // Keep the chart readable: never render more than ~200 bars.
    if (points.length > 200) {
      const step = Math.ceil(points.length / 200);
      points = points.filter((_, i) => i % step === 0);
    }

    const c = chartColors();

    const options = {
      chart: {
        type: "bar" as const,
        toolbar: { show: false },
        background: "transparent",
        foreColor: c.axis,
      },
      plotOptions: {
        bar: { borderRadius: 2, columnWidth: "80%", distributed: true },
      },
      xaxis: {
        categories: points.map((s) => s.tradeIndex),
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: c.axis, fontSize: "11px" },
          formatter: (v: number) => fmtShort(v) + "$",
        },
      },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 4,
      },
      tooltip: {
        theme: isDark ? "dark" : "light",
        y: {
          formatter: (v: number) => `${v.toFixed(2)} $`,
        },
      },
      colors: points.map((s) => pnlColor(s.pnl)),
      legend: { show: false },
      dataLabels: { enabled: false },
    };

    const data = points.map((s) => Math.round(s.absSize * 100) / 100);

    return { options, data };
  }, [analyticsData, isDark]);

  if (!analyticsData || analyticsData.sizingData.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={t.dashboard.widgets.sizingAnalysis} isDark={isDark} compact />
        <CardContent className="flex-1 p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.widgets.sizingAnalysis}
        isDark={isDark}
        compact
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="flex-1 flex flex-col px-4 pb-4 pt-2">
        <p className="mb-1 text-[11px] text-muted-foreground">{t.analytics.sizingDesc}</p>
        {viewMode === "table" ? (
          <div className="flex-1 min-h-0">
            <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.noDataAvailable} />
          </div>
        ) : built ? (
          <div className="flex-1">
            <LazyApexChart
              key={`sizing-analysis-widget-${themeId}`}
              options={built.options}
              series={[{ name: t.analytics.sizingTitle, data: built.data }]}
              type="bar"
              height={220}
            />
          </div>
        ) : (
          <EmptyState label={t.analytics.noDataAvailable} />
        )}
      </CardContent>
    </Card>
  );
}
